import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from "@mui/material";
import { Form, Field } from "react-final-form";
import axios from "axios";

const API_KEY = "AIzaSyCKfUSr5PacGOhWURYgZlgjmgGh4Oc5kjE";

interface ConsentBundelPlaylistModelProps {
  open: boolean;
  onClose: () => void;
  listId: number | null;
  consentBundleId: number | null;
  onSuccess: (data?: any) => void;
  onUploadStart?: () => void;
}

const ConsentBundelPlaylistModel: React.FC<ConsentBundelPlaylistModelProps> = ({
  open,
  onClose,
  listId,
  consentBundleId,
  onSuccess,
  onUploadStart,
}) => {
  const [videoDetails, setVideoDetails] = useState({
    title: "",
    description: "",
    thumbnail: "",
    duration: "",
  });
  const [url, setUrl] = useState("");

  
type VideoPlatform = "youtube" | "tiktok" | null;

const detectVideoPlatform = (url: string): VideoPlatform => {
  if (!url) return null;

  const youtubeRegex =
    /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[a-zA-Z0-9_-]{11}/;

  const tiktokRegex =
    /^(https?:\/\/)?(www\.)?(tiktok\.com\/(@[\w.-]+\/video\/\d+|t\/\w+))/;

  if (youtubeRegex.test(url)) return "youtube";
  if (tiktokRegex.test(url)) return "tiktok";

  return null;
};


 
 const extractYouTubeVideoId = (url: string) => {
  const regex =
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};


 
  const getVideoDetails = async (videoId: string) => {
    const ytUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoId}&key=${API_KEY}`;
    try {
      const response = await fetch(ytUrl);
      const data = await response.json();
      if (data.items.length > 0) {
        const videoData = data.items[0];
        const title = videoData.snippet.title;
        const description = videoData.snippet.description;
        const thumbnail = videoData.snippet.thumbnails.high.url;
        const rawDuration = videoData.contentDetails.duration;
        const duration = formatISODuration(rawDuration);
        return { title, description, thumbnail, duration };
      } else {
        throw new Error("Video not found");
      }
    } catch (error) {
      console.error("Error fetching video details:", error);
      return null;
    }
  };


  const handleScrubVideoDetails = async (url: string, form: any) => {
  const platform = detectVideoPlatform(url);

  if (!platform) {
    resetVideoDetails(form);
    return;
  }

  // YouTube → fetch metadata
  if (platform === "youtube") {
    const videoId = extractYouTubeVideoId(url);
    if (!videoId) {
      resetVideoDetails(form);
      return;
    }

    const details = await getVideoDetails(videoId);
    if (details) {
      setVideoDetails(details);
      form.change("title", details.title);
      form.change("description", details.description);
    } else {
      resetVideoDetails(form);
    }
  }

  if (platform === "tiktok") {
    setVideoDetails({
      title: "TikTok Video",
      description: "",
      thumbnail: "",
      duration: "",
    });
    form.change("title", "TikTok Video");
  }
};


  const resetVideoDetails = (form: any) => {
    form.change("title", "");
    form.change("description", "");
    setVideoDetails({ title: "", description: "", thumbnail: "", duration: "" });
  };



  useEffect(() => {
    if (url) handleScrubVideoDetails(url, {});
  }, [url]);

  useEffect(() => {
    if (!open) {
      setUrl("");
      setVideoDetails({ title: "", description: "", thumbnail: "", duration: "" });
    }
  }, [open]);


  const validate = (values: any) => {
  const errors: any = {};

  if (!values.url) {
    errors.url = "Video URL is required";
    return errors;
  }

  const platform = detectVideoPlatform(values.url);

  if (!platform) {
    errors.url = "Only YouTube or TikTok video links are allowed";
  }

  return errors;
};



  const formatISODuration = (isoDuration: string): string => {
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const matches = isoDuration.match(regex);
    if (!matches) return "0:00";
    const hours = parseInt(matches[1] || "0", 10);
    const minutes = parseInt(matches[2] || "0", 10);
    const seconds = parseInt(matches[3] || "0", 10);
    return hours > 0
      ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
      : `${minutes}:${String(seconds).padStart(2, "0")}`;
  };


  const submitDataYt = async (values: any, onSuccess: () => void) => {
  const platform = detectVideoPlatform(url);

  if (!platform) {
    alert("Only YouTube or TikTok links are allowed");
    return;
  }

  const payload = {
    title: values.title || videoDetails.title,
    description: values.description || videoDetails.description,
    url,
    duration: videoDetails.duration,
    thumbnail: videoDetails.thumbnail,
    platform, 
  };

  try {
    if (onUploadStart) onUploadStart();
    const response = await axios.put(
      `consent_bundle/${consentBundleId}/add_video`,
      payload
    );

    if (response.status === 200) {
      onClose();
      onSuccess(response.data);
    }
  } catch (error) {
    console.error("Error submitting data:", error);
  }
};



  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "650px",
          borderRadius: "15px",
          padding: "20px",
          maxWidth: "100%",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
        Add New YouTube Video
      </DialogTitle>

      <DialogContent>
        <Form
          onSubmit={(values) => submitDataYt(values, onSuccess)}
          validate={validate}
          initialValues={{}}
          render={({ handleSubmit, form, values }) => (
            <form
              onSubmit={async (e) => {
                await handleSubmit(e);
                form.reset();
              }}
            >
              <Box sx={{ mb: 2 }}>
                {/* URL Field */}
                <Field name="url">
                  {({ input, meta }) => (
                    <TextField
                      {...input}
                      label="YouTube Video URL"
                      fullWidth
                      required
                      value={url}
                      onChange={(e) => {
                        setUrl(e.target.value);
                        input.onChange(e);
                      }}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                      sx={{
                        mb: 2,
                        backgroundColor: "#f4f6f8",
                        borderRadius: "8px",
                      }}
                    />
                  )}
                </Field>

                {/* Thumbnail Preview */}
                {videoDetails.thumbnail && (
                  <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                    <img
                      src={videoDetails.thumbnail}
                      alt="Video Thumbnail"
                      style={{ width: "100%", borderRadius: "8px" }}
                    />
                  </Box>
                )}

                {/* Title & Description */}
                {url && videoDetails.thumbnail && (
                  <>
                    <Field name="title">
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          label="Video Title"
                          fullWidth
                          value={values.title || videoDetails.title}
                          onChange={(e) => input.onChange(e)}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                          sx={{
                            mb: 2,
                            backgroundColor: "#f4f6f8",
                            borderRadius: "8px",
                          }}
                        />
                      )}
                    </Field>

                    <Field name="description">
                      {({ input, meta }) => (
                        <TextField
                          {...input}
                          label="Description"
                          fullWidth
                          multiline
                          rows={3}
                          value={values.description || videoDetails.description}
                          onChange={(e) => input.onChange(e)}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                          sx={{
                            mb: 2,
                            backgroundColor: "#f4f6f8",
                            borderRadius: "8px",
                          }}
                        />
                      )}
                    </Field>
                  </>
                )}
              </Box>

              {/* Dialog Actions */}
              <DialogActions>
                <Button
                  onClick={() => {
                    setUrl("");
                    setVideoDetails({
                      title: "",
                      description: "",
                      thumbnail: "",
                      duration: "",
                    });
                    onClose();
                  }}
                  color="primary"
                >
                  Cancel
                </Button>

                <Button type="submit" variant="contained" color="primary">
                  Add
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ConsentBundelPlaylistModel;
